import { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import { parseISODate } from '@ygtechnologies/rubus-frontend-utilities';

interface UnitThreadInfo {
    unreadThreads: number;
    unreadMessages: number;
    lastMessageDate: Date;
}

interface UnitUnreadMap {
    [residentialUnitId: number]: UnitThreadInfo;
}

export function useUnreadMessagesByUnit(): [UnitUnreadMap, (residentialUnitId: number) => UnitThreadInfo] {
    const commentThreads = useSelector((state: IState) => state.seller.projectOverview.comments);

    const unreadThreadsByUnit = useMemo(() => {
        const unreadThreads = commentThreads ?? [];
        return unreadThreads.reduce((acc, current) => {
            if (!current.residentialUnitId) return acc;

            const value: UnitThreadInfo = acc[current.residentialUnitId] || {
                unreadThreads: 0,
                unreadMessages: 0,
                lastMessageDate: null,
            };

            const messageDate = current.lastMessageDate ? parseISODate(current.lastMessageDate) : null;
            const newValue: UnitThreadInfo = {
                unreadThreads: value.unreadThreads + (current.unreadMessages > 0 ? 1 : 0),
                unreadMessages: value.unreadMessages + current.unreadMessages || 0,
                lastMessageDate: messageDate && messageDate > value.lastMessageDate ? messageDate : value.lastMessageDate,
            };

            acc[current.residentialUnitId] = newValue;
            return acc;
        }, {}) as UnitUnreadMap;
    }, [commentThreads]);

    const getter = useCallback((residentialUnitId: number) => unreadThreadsByUnit[residentialUnitId], [unreadThreadsByUnit]);

    return [unreadThreadsByUnit, getter];
}

export { default } from './ProjectResidentialUnits';
